import { queryOptions } from '@tanstack/react-query';

import { queryClient } from '@/services/query-client';

import { fetchClasses } from '../data-provider';
import type { Class, Classes, ClassFilters } from '../models';
import { CACHE_KEYS } from '../types';
import { logger } from '../utils/logger';

/**
 * Internal class lookup cache used to resolve class references
 * without repeatedly requesting the same class data.
 *
 * Backed by React Query today, but can be migrated to another storage
 * implementation (e.g. IndexedDB) without changing consumers.
 */

export const classesQueryOptions = (
  filters: Partial<ClassFilters> = {},
  { enabled = true }: { enabled?: boolean } = {},
) =>
  queryOptions({
    enabled,
    queryFn: async ({ signal }) => {
      const classes = await fetchClasses(filters, { signal });
      put(classes.list);
      return classes;
    },
    queryKey: CACHE_KEYS.classes(filters),
    // biome-ignore lint/style/noMagicNumbers: 5 minutes
    staleTime: 1000 * 60 * 5,
  });

const INTERNAL_CLASSES_CACHE_KEY = btoa('internal-classes-cache-key');

const INTERNAL_QUERY_CLASSES_KEY = classesQueryOptions({ codeDesc: INTERNAL_CLASSES_CACHE_KEY }).queryKey;

const getClassFallback = (classId: string): Class => {
  logger.warn(`Class "${classId}" was not found. Verify the data model.`);
  return {
    codeDesc: classId,
    id: classId,
    label: classId,
    lookupCodeType: 'CLASS',
  };
};

export const put = async (classes: Class[]) => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_CLASSES_KEY);

  const newClasses = classes.reduce(
    (acc, freightClass) => {
      acc[String(freightClass.id)] = freightClass;
      return acc;
    },
    { ...(data?.map ?? {}) },
  );

  queryClient.setQueryData(INTERNAL_QUERY_CLASSES_KEY, {
    list: Object.values(newClasses),
    map: newClasses,
  });
};

export const getAll = (): Classes | undefined => {
  return queryClient.getQueryData(INTERNAL_QUERY_CLASSES_KEY);
};

export const find = async (classId: string): Promise<Class> => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_CLASSES_KEY);

  const freightClass = data?.map[classId];
  if (freightClass) {
    return freightClass;
  }

  try {
    const newData = await queryClient.query(classesQueryOptions({ search: { codeId: classId } }));
    if (newData.map[classId]) {
      void put(newData.list);
      return newData.map[classId];
    }
  } catch {
    return getClassFallback(classId);
  }

  return getClassFallback(classId);
};

export const findAll = async (classIds: string[]): Promise<Class[]> => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_CLASSES_KEY);

  const missingIds: string[] = [];
  const classes: Class[] = [];

  for (const classId of classIds) {
    const freightClass = data?.map[classId];
    if (freightClass) {
      classes.push(freightClass);
    } else {
      missingIds.push(classId);
    }
  }

  if (!missingIds.length) {
    return classes;
  }

  try {
    const newData = await queryClient.query(classesQueryOptions({ search: { codeId: missingIds.join(',') } }));
    void put(newData.list);
    for (const missingId of missingIds) {
      const freightClass = newData.map[missingId];
      if (freightClass) {
        classes.push(freightClass);
      } else {
        classes.push(getClassFallback(missingId));
      }
    }
  } catch {
    for (const missingId of missingIds) {
      classes.push(getClassFallback(missingId));
    }
  }

  return classes;
};
